// The region, band, grain and recency vocabulary shared by the native Meteoric
// modules that read a UK / EU / MENA split (Units Sold, Profit Yield).
//
// One copy on purpose: two modules that each carry their own freshness
// thresholds or region labels drift, and then the same market reads "Live" on
// one screen and "Lagging" on the next. Module-specific vocabulary (Units Sold's
// `warn` severity vs Profit Yield's `warning`) stays in the module.

import type { FreshnessMeta } from "@/lib/api";
import type { Tone } from "@/lib/tone";

/** The shell's S8 freshness meta, re-exported so the envelope type has one source. */
export type { FreshnessMeta };

/** A read API response: the S8 envelope with `meta` required, not optional. */
export type Enveloped<T> = { data: T; meta: FreshnessMeta };

export const REGIONS = ["uk", "eu", "mena"] as const;
export type Region = (typeof REGIONS)[number];

export function isRegion(value: string): value is Region {
  return (REGIONS as readonly string[]).includes(value);
}

const REGION_LABELS: Record<string, string> = {
  uk: "UK",
  eu: "EU",
  mena: "MENA",
};

export function regionLabel(region: string): string {
  return REGION_LABELS[region.toLowerCase()] ?? region.toUpperCase();
}

/**
 * The region an attention item's deep link names, or `null` when it names no
 * region (an overview-level item). Reads the link's path segments whole, not
 * as a substring, so `eu` never matches inside `mena`.
 */
export function regionFromDeepLink(deepLink: string): Region | null {
  const path = deepLink.toLowerCase().split("?")[0] ?? "";
  const segments = path.split("/");
  for (const region of REGIONS) {
    if (segments.includes(region)) return region;
  }
  return null;
}

/** The band vocabulary the producer emits. `unknown` is a real value, not a gap. */
export type Band = "Critical" | "@ Risk" | "Good" | "Excellent" | "Outstanding" | "unknown";

/**
 * A band's tone, NOT a colour.
 *
 * Meteoric painted bands green/amber/red from its own `--color-ks-pass/warn/fail`
 * tokens. This portal's brand layer has no green and no amber, and its one rule
 * about red is explicit: danger and nothing else (`src/styles/global.css`). So a
 * band is carried by its written name plus a badge weight, and only `Critical`
 * is allowed to be red. That is also the more legible encoding - the band name
 * is already the word a reader would say, and it survives a colourblind reader,
 * a greyscale print, and a screenshot pasted into Slack.
 */
export function bandTone(band: Band): Tone {
  if (band === "Critical") return "critical";
  if (band === "@ Risk") return "watch";
  if (band === "Good" || band === "Excellent" || band === "Outstanding") return "ok";
  return "neutral";
}

export type Grain = "daily" | "weekly" | "monthly";

export const GRAINS: Grain[] = ["daily", "weekly", "monthly"];

export function isGrain(value: unknown): value is Grain {
  return typeof value === "string" && (GRAINS as string[]).includes(value);
}

/** Whole days since an ISO date; null when the date is absent or unreadable. */
export function daysSince(isoDate: string | null): number | null {
  if (!isoDate) return null;
  const then = Date.parse(isoDate);
  if (Number.isNaN(then)) return null;
  return Math.floor((Date.now() - then) / 86_400_000);
}

export function regionsFrom(
  freshness: Record<string, string | null>,
): Array<{ region: Region; date: string | null }> {
  return REGIONS.map((region) => ({ region, date: freshness[region] ?? null }));
}

/**
 * How far behind "today" a region's newest data point may fall before the strip
 * says so. Both numbers are STATED on screen, because a threshold a reader
 * cannot see is a threshold they cannot trust - and they are the same ones
 * Meteoric used, so a region does not change status simply by being read here.
 */
export const DATA_FRESH_MAX_DAYS = 3;
export const DATA_WARN_MAX_DAYS = 9;

export type FreshnessState = { tone: Tone; word: string; ageLabel: string | null };

/**
 * A region's recency as a word, not a colour.
 *
 * "No data yet" is deliberately its own state rather than the worst one: a
 * market that has never reported and a market that stopped reporting are
 * different problems, and only the second is a regression.
 */
export function freshnessState(isoDate: string | null): FreshnessState {
  if (!isoDate) return { tone: "neutral", word: "No data yet", ageLabel: null };
  const lag = daysSince(isoDate);
  if (lag === null) return { tone: "neutral", word: "Date unreadable", ageLabel: null };
  const ageLabel = lag <= 0 ? "up to date" : `${lag} ${lag === 1 ? "day" : "days"} behind`;
  if (lag > DATA_WARN_MAX_DAYS) return { tone: "critical", word: "Stale", ageLabel };
  if (lag > DATA_FRESH_MAX_DAYS) return { tone: "watch", word: "Lagging", ageLabel };
  return { tone: "ok", word: "Live", ageLabel };
}
